import {
  MIN_TRIM_SECONDS,
  clamp,
  el,
  formatSpeed,
  formatTime,
  refreshMetrics,
  roundTo,
  signalPlanChange,
  state,
} from "./core.js"

const activeTouches = new Map()
let pinch = null

export function renderTimeline(currentTime = state.previewCurrentTime) {
  if (!state.sourceDuration) return
  const current = clamp(Number(currentTime) || state.trimStart, 0, state.sourceDuration)
  const start = state.trimStart / state.sourceDuration * 100
  const end = state.trimEnd / state.sourceDuration * 100
  el.timelineDimLeft.style.width = `${start}%`
  el.timelineDimRight.style.width = `${100 - end}%`
  el.timelineSelection.style.left = `${start}%`
  el.timelineSelection.style.width = `${Math.max(0, end - start)}%`
  el.startHandle.style.left = `${start}%`
  el.endHandle.style.left = `${end}%`
  el.seekHead.style.left = `${current / state.sourceDuration * 100}%`
  el.trimStartInput.value = state.trimStart.toFixed(2)
  el.trimEndInput.value = state.trimEnd.toFixed(2)
  el.trimStartInput.max = Math.max(0, state.trimEnd - MIN_TRIM_SECONDS).toFixed(2)
  el.trimEndInput.min = Math.min(state.sourceDuration, state.trimStart + MIN_TRIM_SECONDS).toFixed(2)
  el.trimEndInput.max = state.sourceDuration.toFixed(2)
  el.trimLengthValue.textContent = formatTime(Math.max(0, state.trimEnd - state.trimStart))
}

function timelineAnchorRatio(clientX) {
  const rect = el.timelineTrack.getBoundingClientRect()
  return clamp((clientX - rect.left) / Math.max(1, rect.width), 0, 1)
}

export function updateTimelineWidth(anchorClientX = null, anchorRatio = null) {
  const viewportRect = el.timelineViewport.getBoundingClientRect()
  const viewportWidth = Math.max(1, viewportRect.width)
  const available = Math.max(1, viewportWidth - 48)
  const ratio = anchorRatio ?? (state.sourceDuration ? clamp(state.previewCurrentTime / state.sourceDuration, 0, 1) : 0.5)
  const clientX = anchorClientX ?? viewportRect.left + viewportWidth / 2
  const localX = clientX - viewportRect.left
  el.timelineTrack.style.width = `${Math.max(available, Math.round(available * state.zoom))}px`
  el.zoomReadout.textContent = `${state.zoom.toFixed(1)}×`
  el.zoomSlider.value = String(Math.round(state.zoom * 10))
  const trackLeft = el.timelineTrack.offsetLeft
  const desired = trackLeft + ratio * el.timelineTrack.offsetWidth - localX
  el.timelineViewport.scrollLeft = clamp(desired, 0, Math.max(0, el.timelineViewport.scrollWidth - viewportWidth))
}

export function setZoom(value, anchorClientX = null, forcedRatio = null) {
  const ratio = forcedRatio ?? (anchorClientX == null ? null : timelineAnchorRatio(anchorClientX))
  state.zoom = clamp(roundTo(Number(value), 2), 1, 40)
  updateTimelineWidth(anchorClientX, ratio)
}

export function setSpeed(value, shouldRebuild = true) {
  state.speed = clamp(roundTo(Number(value), 3), 0.1, 8)
  el.speedSlider.value = String(roundTo(state.speed, 1))
  el.speedReadout.textContent = formatSpeed(state.speed)
  document.querySelectorAll("[data-speed]").forEach((button) => {
    button.classList.toggle("active", Math.abs(Number(button.dataset.speed) - state.speed) < 0.0001)
  })
  refreshMetrics()
  if (shouldRebuild) signalPlanChange("speed")
}

export function setDirection(direction, shouldRebuild = true) {
  if (!["forward", "reverse", "rebound"].includes(direction)) return
  state.direction = direction
  document.querySelectorAll("[data-direction]").forEach((button) => {
    button.classList.toggle("active", button.dataset.direction === direction)
  })
  refreshMetrics()
  if (shouldRebuild) signalPlanChange("direction")
}

function pointerTime(clientX) {
  const rect = el.timelineTrack.getBoundingClientRect()
  return clamp((clientX - rect.left) / Math.max(1, rect.width), 0, 1) * state.sourceDuration
}

function notifySeek(time) {
  state.previewCurrentTime = time
  dispatchEvent(new CustomEvent("converter-seek", { detail: { time } }))
  renderTimeline(time)
}

function applyDrag(role, clientX) {
  if (!state.sourceDuration) return
  const time = pointerTime(clientX)
  if (role === "start") {
    state.trimStart = clamp(time, 0, state.trimEnd - MIN_TRIM_SECONDS)
    notifySeek(state.trimStart)
    refreshMetrics()
    signalPlanChange("trim")
  } else if (role === "end") {
    state.trimEnd = clamp(time, state.trimStart + MIN_TRIM_SECONDS, state.sourceDuration)
    notifySeek(Math.min(state.trimEnd, state.sourceDuration - 0.001))
    refreshMetrics()
    signalPlanChange("trim")
  } else if (role === "seek") {
    notifySeek(clamp(time, state.trimStart, Math.max(state.trimStart, state.trimEnd - 0.001)))
  }
}

function beginTimelinePointer(event) {
  if (!state.sourceDuration) return
  if (event.pointerType === "touch") {
    activeTouches.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (activeTouches.size === 2) {
      const [a, b] = [...activeTouches.values()]
      pinch = {
        distance: Math.hypot(a.x - b.x, a.y - b.y),
        zoom: state.zoom,
        ratio: timelineAnchorRatio((a.x + b.x) / 2),
      }
      state.dragRole = null
      state.dragPointerId = null
      return
    }
  }

  event.preventDefault()
  state.dragRole = event.target.closest("[data-role]")?.dataset.role || "pan"
  state.dragPointerId = event.pointerId
  state.dragMoved = false
  state.dragStartX = event.clientX
  state.dragStartScroll = el.timelineViewport.scrollLeft
  el.timelineTrack.setPointerCapture?.(event.pointerId)
  if (state.dragRole !== "pan") applyDrag(state.dragRole, event.clientX)
}

function moveTimelinePointer(event) {
  if (event.pointerType === "touch" && activeTouches.has(event.pointerId)) {
    activeTouches.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (activeTouches.size >= 2 && pinch) {
      event.preventDefault()
      const [a, b] = [...activeTouches.values()]
      const distance = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y))
      const midpoint = (a.x + b.x) / 2
      setZoom(pinch.zoom * distance / Math.max(1, pinch.distance), midpoint, pinch.ratio)
      return
    }
  }

  if (!state.dragRole || event.pointerId !== state.dragPointerId) return
  event.preventDefault()
  const delta = event.clientX - state.dragStartX
  if (Math.abs(delta) > 4) state.dragMoved = true
  if (state.dragRole === "pan") {
    el.timelineViewport.scrollLeft = state.dragStartScroll - delta
  } else {
    applyDrag(state.dragRole, event.clientX)
  }
}

function finishTimelinePointer(event) {
  if (event.pointerType === "touch") {
    activeTouches.delete(event.pointerId)
    if (activeTouches.size < 2) pinch = null
  }
  if (event.pointerId !== state.dragPointerId) return
  if (state.dragRole === "pan" && !state.dragMoved) applyDrag("seek", event.clientX)
  state.dragRole = null
  state.dragPointerId = null
}

function updateTrim(role, value) {
  if (!state.sourceDuration || !Number.isFinite(Number(value))) return
  if (role === "start") {
    state.trimStart = clamp(Number(value), 0, state.trimEnd - MIN_TRIM_SECONDS)
    notifySeek(state.trimStart)
  } else {
    state.trimEnd = clamp(Number(value), state.trimStart + MIN_TRIM_SECONDS, state.sourceDuration)
    notifySeek(Math.min(state.trimEnd, state.sourceDuration - 0.001))
  }
  renderTimeline()
  refreshMetrics()
  signalPlanChange("trim")
}

export function setupTimeline() {
  document.querySelectorAll("[data-direction]").forEach((button) => button.addEventListener("click", () => setDirection(button.dataset.direction)))
  document.querySelectorAll("[data-speed]").forEach((button) => button.addEventListener("click", () => setSpeed(Number(button.dataset.speed))))
  el.speedSlider.addEventListener("input", () => setSpeed(Number(el.speedSlider.value)))
  el.zoomSlider.addEventListener("input", () => setZoom(Number(el.zoomSlider.value) / 10))
  el.zoomOut.addEventListener("click", () => setZoom(state.zoom / 1.35))
  el.zoomIn.addEventListener("click", () => setZoom(state.zoom * 1.35))

  el.timelineViewport.addEventListener("wheel", (event) => {
    if (!state.sourceDuration) return
    event.preventDefault()
    const factor = Math.exp(-event.deltaY * 0.0025)
    setZoom(state.zoom * factor, event.clientX)
  }, { passive: false })

  el.timelineTrack.addEventListener("pointerdown", beginTimelinePointer)
  el.timelineTrack.addEventListener("pointermove", moveTimelinePointer)
  el.timelineTrack.addEventListener("pointerup", finishTimelinePointer)
  el.timelineTrack.addEventListener("pointercancel", finishTimelinePointer)
  el.timelineViewport.addEventListener("pointermove", moveTimelinePointer)
  el.timelineViewport.addEventListener("pointerup", finishTimelinePointer)
  el.timelineViewport.addEventListener("pointercancel", finishTimelinePointer)

  el.trimStartInput.addEventListener("change", () => updateTrim("start", el.trimStartInput.value))
  el.trimEndInput.addEventListener("change", () => updateTrim("end", el.trimEndInput.value))
  addEventListener("resize", () => updateTimelineWidth())
  addEventListener("converter-preview-time", (event) => renderTimeline(event.detail.time))
}
